/**
 * Every keyboard shortcut this app has, and nothing else.
 *
 * This file used to *hold* the list and had to be maintained against the
 * handler in `app.tsx` by hand -- an arrangement it documented, apologised for,
 * and then drifted from anyway. The list now comes from `lib/shortcuts.ts`,
 * which is the same table the handler dispatches from, so a key drawn here is a
 * key that works and a key that works is drawn here. What is left in this file
 * is the sheet: layout, the modal behaviour, and how a combination is spelled
 * out for a screen reader.
 */

import { Fragment } from "preact";
import { useId } from "preact/hooks";

import * as nav from "../lib/navigation";
import * as shortcuts from "../lib/shortcuts";
import { useDialog } from "./dialog";
import * as Icon from "./Icons";

export function Help() {
  const close = () => (nav.helpOpen.value = false);
  const sheet = useDialog<HTMLDivElement>(close);
  const titleId = useId();

  return (
    <div class="overlay" onClick={close}>
      <div
        ref={sheet}
        class="sheet help"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="help-head">
          <strong id={titleId}>Keyboard shortcuts</strong>
          <button class="icon-btn" aria-label="Close the shortcuts" onClick={close}>
            <Icon.Close />
          </button>
        </div>

        {/*
          The rule that makes the rest of the page make sense, and the one thing
          nobody can work out by pressing keys: a shortcut acts on the ticked
          messages when there are any, which is what makes ticking forty rows
          and pressing `e` once do what it looks like it will do.
        */}
        <p class="help-lede">
          Keys act on the ticked messages, or the one under the cursor. Shortcuts pause while you
          type.
        </p>

        {shortcuts.GROUPS.map((group) => (
          <section key={group.title} class="help-group">
            <h3>{group.title}</h3>
            {group.note && <p class="help-note">{group.note}</p>}
            <div class="keys">
              {group.keys.map((shortcut) => (
                <Fragment key={shortcut.id}>
                  <span class="help-keys">
                    {/* Keyed by position, not by the key itself: `f then f` is
                        two children spelled the same, and keying on the letter
                        makes them one as far as the reconciler is concerned. */}
                    {shortcut.press.map((press, i) => (
                      <Fragment key={i}>
                        {i > 0 && <span class="help-join">{shortcut.chord ? "then" : "or"}</span>}
                        <Combo press={press} />
                      </Fragment>
                    ))}
                  </span>
                  <span>{shortcut.what}</span>
                </Fragment>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * One press, with each held modifier drawn as its own key.
 *
 * `<kbd>Shift</kbd>+<kbd>R</kbd>` rather than a single `<kbd>Shift + R</kbd>`,
 * because they are two physical keys and a screen reader reads them as two
 * things to press either way -- but only the split version says which one is
 * held.
 */
function Combo({ press }: { press: shortcuts.Press }) {
  return (
    <span class="help-combo">
      {shortcuts.spell(press).map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <span class="help-plus">+</span>}
          <kbd>{part}</kbd>
        </Fragment>
      ))}
    </span>
  );
}
